import { Extension } from "@tiptap/core";
import { TextSelection } from "@tiptap/pm/state";
import { isPageBreak, startsPage } from "Components/RichTextEditor/extensions/PageBreak";
import { BUILT_IN_ITEMS } from "Components/RichTextEditor/toolbarItems";

/**
 * Page break and New page for notices and factsheets.
 *
 * Their converters (bac/html_docx.py: build_notice_docx_bytes for notices,
 * html_to_docx_bytes and then LibreOffice for factsheets) add no running
 * header, so the page carries its own: a fresh page opens with the case
 * number, bold and centred, on its first line. Everything written is markup
 * both converters already read and noticeHtml.js's allowlist keeps - a
 * paragraph with `text-align: center` and `page-break-before: always`, and
 * <strong>.
 *
 * The page always starts at the top level, after the block the caret is in
 * (or in place of it, if that block is an empty line): html_docx honours a
 * page break on a top-level paragraph, not on one inside a table cell, and a
 * factsheet is mostly one table.
 */

// "AC No. 12/2026" - the case id usually carries the prefix already. Same
// rule as the order sheet's running header (printOrderSheet.js).
export const caseHeaderText = (caseNumber) => {
  const ac = String(caseNumber || "").trim();
  if (!ac) return "";
  return /\bNo\./.test(ac) ? ac : `AC No. ${ac}`;
};

export const CaseNumberPage = Extension.create({
  name: "caseNumberPage",
  // Its Ctrl+Enter ahead of PageBreak's.
  priority: 101,

  addOptions() {
    return {
      // Read at the moment the page is added: the surface may learn the
      // case number after the editor has mounted.
      getCaseNumber: () => "",
    };
  },

  addCommands() {
    return {
      /** A new page: the case-number line, then an empty line for the caret. */
      startCaseNumberPage: () => ({ state, tr, dispatch }) => {
        const { schema } = state;
        const { paragraph } = schema.nodes;
        const { $from } = state.selection;
        if (!paragraph || $from.depth < 1) return false;

        const heading = caseHeaderText(this.options.getCaseNumber());
        const bold = schema.marks.bold;
        const page = [
          paragraph.create(
            { textAlign: "center", style: "page-break-before: always" },
            heading ? schema.text(heading, bold ? [bold.create()] : []) : null
          ),
          paragraph.create(),
        ];

        if (dispatch) {
          const top = $from.node(1);
          const to = $from.after(1);
          const from =
            top.type === paragraph && !top.content.size ? $from.before(1) : to;
          tr.replaceWith(from, to, page);
          tr.setSelection(
            TextSelection.create(tr.doc, from + page[0].nodeSize + 1)
          ).scrollIntoView();
        }
        return true;
      },

      /**
       * Take a page away again: the case-number line goes entirely; any other
       * page break just loses the break (PageBreak's removePageBreak).
       */
      removeCaseNumberPage: () => ({ state, tr, dispatch, commands }) => {
        const { $from } = state.selection;
        const heading = caseHeaderText(this.options.getCaseNumber());
        for (let depth = $from.depth; depth > 0; depth -= 1) {
          const node = $from.node(depth);
          if (!node.isTextblock) continue;
          if (startsPage(node) && heading && node.textContent.trim() === heading) {
            if (dispatch) tr.delete($from.before(depth), $from.after(depth));
            return true;
          }
          break;
        }
        return commands.removePageBreak();
      },
    };
  },

  addKeyboardShortcuts() {
    return {
      "Mod-Enter": () => this.editor.commands.startCaseNumberPage(),
    };
  },
});

/** The Page break and New page buttons for these surfaces. */
export const CASE_NUMBER_PAGE_ITEMS = {
  pagebreak: {
    ...BUILT_IN_ITEMS.pagebreak,
    // Toolbar.js appends the shortcut from shortcuts.js.
    title: "Page break - start a new page with the case number at the top, or remove this one",
    run: (e) =>
      isPageBreak(e)
        ? e.chain().focus().removeCaseNumberPage().run()
        : e.chain().focus().startCaseNumberPage().run(),
  },
  newpage: {
    ...BUILT_IN_ITEMS.newpage,
    title: "New page - start a new page with the case number at the top",
    run: (e) => e.chain().focus().startCaseNumberPage().run(),
  },
};
